import type {
    FetchPolicy,
    Result,
    ValidCacheData,
} from "@khanacademy/wonder-blocks-data";
import type {GAEModule, GqlOperation} from "@khan/data-gql-core";

export enum Persist {
    /* A deploy *and* a publish will flush the cached value. Use this option
       when the result might change as a result of a publish. */
    UntilPublish = "persist_until_publish",
    /* A deploy will flush the cached value, but a publish will not. Use this
       option when the result cannot change as a result of a publish. */
    AcrossPublish = "persist_across_publish",
    /* Neither a deploy nor a publish will flush the cached value. Use this
       option when the result cannot change as a result of a deploy or of a
       publish. */
    AcrossDeploy = "persist_across_deploy",
}

/**
 * Context for GraphQL requests.
 *
 * This is the version that developers will interact with.
 * It provides stronger, more useful typing than the type that is consumed
 * by Wonder Blocks Data.
 */
export interface GqlContext {
    module: GAEModule;
    targetLocale: string;
    curriculum?: string;
    fastlyCaching?: Persist;
}

/**
 * Context for GraphQL requests as used with Wonder Blocks Data.
 *
 * This is equivalent to `GqlContext` but each property is a string.
 */
export type GqlContextRecord = {
    [K in keyof GqlContext]: GqlContext[K] extends undefined
        ? string | undefined
        : string;
};

/**
 * Allows for the optional specification of GraphQL operation context.
 *
 * This can be used by individual operations to override page and local
 * defaults. See uses in `useQuery` and `useMutation` for examples.
 *
 * This excludes `fastlyCaching` as that is only valid in very specific
 * situations and not subject to override.
 */
export interface PartialGqlContext {
    /**
     * Specifies a curriculum to use.
     */
    curriculum?: GqlContext["curriculum"];
    /**
     * Specifies the module to use when serving the query.
     * See the `GAEModule` type for possible values.
     */
    module?: GqlContext["module"];
    /**
     * Specifies the target locale for a query.
     * Useful for targetting a different localized topic tree, for example.
     */
    targetLocale?: GqlContext["targetLocale"];
}

/**
 * Options for configuring a GQL fetch.
 */
export interface GqlFetchOptions<TVariables extends Record<any, any>> {
    variables?: TVariables;
    context?: Partial<GqlContext>;
}

/**
 * Functions that make fetches of GQL operations.
 */
export type GqlFetchFn = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    options?: GqlFetchOptions<TVariables>,
) => Promise<TData>;

export type GqlQueryOptions<TData extends ValidCacheData, TVariables> = {
    /**
     * Callback invoked when the operation has resolved and the cached result
     * is updated. It is passed the latest cached value (which is also the
     * value that the hook would return on the next invocation).
     *
     * If omitted, the hook will manipulate state internally to trigger
     * a component update.
     *
     * NOTE: We currently do not invoke this if another hook instance modified
     * the same cache value. This is because we do not expect that to be a use
     * we would encounter. Let frontend-infra know if you believe you need this.
     */
    cacheUpdated?: (current: Result<TData>) => void;
    /**
     * Whether the query result should be reset when the requestId changes.
     * See `retainResultOnChange` property in useCachedEffect.
     */
    retainResultOnChange?: boolean;
    /**
     * When true, regardless of the cache state, the query will not be
     * executed. The current cache state will be returned, which will be
     * {status: "loading"} if the query is not in the cache.
     */
    skip?: boolean;
    /**
     * The variables your query needs to execute.
     */
    variables?: TVariables;
    /**
     * The fetch policy to use when fulfilling the hook's data.
     *
     * This defaults to FetchPolicy.CacheBeforeNetwork.
     *
     * - CacheBeforeNetwork
     *   If the data is in the cache, the hook will return that result;
     *   otherwise, a fetch is initiated for the data, and a `loading` result
     *   is returned. Once the fetch completes, the cache is updated, and the
     *   hook will trigger a re-render with the new result, or, if a
     *   `cacheUpdated` callback is provided, a re-render will not occur and
     *   instead `cacheUpdated` will be invoked with the new result.
     *   This only invokes a fetch if there is no cached value, or a refetch
     *   is manually triggered via the returned refetch function.
     *   This is equivalent to `cache-first` in Apollo Client.
     *
     * - CacheOnly
     *   If the data is in the cache, the hook will return that result;
     *   otherwise, it will return a `no-data` status. This never invokes a
     *   fetch. This is equivalent to `cache-only` in Apollo Client.
     *
     * - NetworkOnly
     *   This ignores any existing cached result and instead will always fetch
     *   from the server. This returns a `loading` result while the request is
     *   in-flight, and then re-renders with the result once the fetch
     *   completes. The result is added to the cache.
     *   This is equivalent to `network-only` in Apollo Client.
     *
     * - CacheAndNetwork
     *   This uses the cached result while waiting on a fresh network request.
     *   This is different to `CacheBeforeNetwork` in that it will always
     *   initiate a network request, even if there is a cached value.
     *   This returns the cached value immediately if it exists, and then
     *   re-renders with the new result once the fetch completes.
     *   This always invokes a fetch, even if there is a cached value. It
     *   only refetches after being manually triggered via the returned refetch
     *   function. If there is no cached value, it will return a `loading`
     *   result while the request is in-flight.
     *   This is equivalent to `cache-and-network` in Apollo Client.
     */
    fetchPolicy?: FetchPolicy;
} & PartialGqlContext;

/**
 * A function to invoke a mutation.
 */
export type MutateFn<
    TData extends ValidCacheData,
    TVariables extends Record<any, any>,
> = (
    /**
     * The variables for the mutation.
     */
    variables?: TVariables | null | undefined,
    /**
     * An object to modify the default context when performing the mutation.
     */
    context?: PartialGqlContext,
) => Promise<Result<TData>>;
